import React, { useEffect, useRef, useState } from "react";
import { generateReport, createAiSearchCancellation } from "../../libs/API";
import "./SearchSessionReport.css";
import { reportPresentation, reportStatusMessage } from "./reportPresentation";
import { downloadReportPdf } from "./reportPdf";

const terminalStatuses = ["SESSION_EXPIRED", "SESSION_NOT_ACCESSIBLE", "EMPTY_AUTHORIZED_SCOPE"];
const reportStatuses = ["SUCCEEDED", "PARTIAL", "DEGRADED", "NO_FINDINGS", "TIMED_OUT", "FAILED",
  "INVALID_REQUEST", "SELECTION_REQUIRED", "SENSITIVE_DENIED", ...terminalStatuses];
const statusMessage = status => reportStatusMessage(status) || "The report could not be generated. You can retry.";

function Statements({ title, values }) {
  return values.length ? <section className="report-notes"><h5>{title}</h5>
    <ul>{values.map((value, index) => <li key={index}>{value}</li>)}</ul></section> : null;
}

function Findings({ findings }) {
  return findings.length ? <ul className="report-findings">{findings.map((finding, index) => <li key={index}>
    {finding.label && <h6>{finding.label}</h6>}
    <p className="report-source-text">{finding.content}</p>
    {finding.citations.length ? <details className="report-citation">
      <summary>Sources ({finding.citations.length})</summary>
      {finding.citations.map((citation, ci) => <div className="report-source" key={ci}>
        <p><strong>{citation.documentLabel}</strong></p>
        <blockquote>{citation.quote}</blockquote>
        {citation.page && <p className="report-note">{citation.pageLabel || `Physical page ${citation.page}`}</p>}
        {citation.sourceNote && <p className="report-note">{citation.sourceNote}</p>}
      </div>)}
    </details> : <p className="report-note">Citation unavailable for this finding.</p>}
    {finding.references.length > 0 && <ul className="report-note" aria-label="Supporting documents">
      {finding.references.map((reference, ri) => <li key={ri}>{reference.documentLabel}</li>)}
    </ul>}
  </li>)}</ul> : null;
}

export function ReportResult({ response, request = "", originalSearch = "", errorShown = false }) {
  const report = reportPresentation(response);
  const [generatedAt] = useState(() => new Date());
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const exportingRef = useRef(false);
  const downloadable = ["SUCCEEDED", "PARTIAL", "DEGRADED", "NO_FINDINGS", "TIMED_OUT", "FAILED"].includes(response.status) &&
    (response.status === "NO_FINDINGS" || report.executiveSummary || report.documents.some(document => document.findings.length) || report.crossDocumentFindings.length);
  const download = async () => {
    if (exportingRef.current) return;
    exportingRef.current = true; setExporting(true); setExportError("");
    try { await downloadReportPdf(response, { request, originalSearch, generatedAt }); }
    catch (_) { setExportError("The PDF could not be downloaded. Your report is still available below. Please try again."); }
    finally { exportingRef.current = false; setExporting(false); }
  };
  return <section aria-label="Generated report" className="report-result">
    <header className="report-cover">
      <p className="report-eyebrow">AI REPORT</p>
      <h4>Search Results Analysis</h4>
      {report.documentCount !== null && <p>Based on {report.documentCount} current search {report.documentCount === 1 ? "result" : "results"}</p>}
      {report.analyzedCount !== null && <p className="report-note">{report.analyzedCount} documents analyzed</p>}
      {report.scopeDescription && <p className="report-note">{report.scopeDescription}</p>}
      <p className="report-note">Generated {generatedAt.toLocaleString()}</p>
      {originalSearch && <div className="report-request"><strong>Original search</strong><p>{originalSearch}</p></div>}
      {request && <div className="report-request"><strong>Report request</strong><p>{request}</p></div>}
      {downloadable && <button type="button" className="chat-search-ai-action" disabled={exporting} onClick={download}>
        {exporting ? "Preparing PDF..." : "Download PDF"}</button>}
      {exportError && <p role="alert">{exportError}</p>}
    </header>
    {report.notice && !errorShown && <p className="report-status-warning">{report.notice}</p>}
    {report.executiveSummary && <section className="report-section"><h5>Executive Summary</h5><p className="report-source-text">{report.executiveSummary}</p></section>}
    {report.crossDocumentFindings.length > 0 && <section className="report-section" aria-label="Cross-document findings">
      <h5>Cross-Document Observations</h5><Findings findings={report.crossDocumentFindings} />
    </section>}
    {report.documents.length > 0 && <section className="report-section" aria-label="Report documents"><h5>Document Analysis</h5>
      {report.documents.map(document => <article className="report-document" key={document.identity}
        aria-label={`Report document ${document.identity}`}>
        <h6>{document.name}</h6>
        <details className="report-note"><summary>Document details</summary><p>Document {document.identity}</p></details>
        {document.notice && <p className="report-note">{document.notice}</p>}
        <Findings findings={document.findings} />
        <Statements title="Missing Information" values={document.missingInformation} />
      </article>)}
    </section>}
    <Statements title="Missing Information" values={report.missingInformation} />
    <Statements title="Limitations" values={report.limitations} />
    {report.documents.length > 0 && <section className="report-section"><h5>Sources</h5>
      <ul className="report-source-list">{report.documents.map(document => <li key={document.identity}>
        {document.name}
      </li>)}</ul>
    </section>}
    {report.pageNavigationNote && <p className="report-note">{report.pageNavigationNote}</p>}
    {report.coverageNote && <p className="report-note report-coverage">{report.coverageNote}</p>}
  </section>;
}

function validResponse(value, session) {
  return value && value.searchSessionId === session && reportStatuses.includes(value.status) &&
    Array.isArray(value.documents) && value.documents.every(document => document &&
      Number.isInteger(document.templateId) && document.templateId > 0 &&
      Number.isInteger(document.fileId) && document.fileId > 0 && Array.isArray(document.findings));
}

function ReportPanel({ searchSessionId, disabled, originalSearch = "", openRequest, hideOpenButton }) {
  const [searchContext] = useState(originalSearch); // Snapshot display context when this session mounts.
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [needsNewSearch, setNeedsNewSearch] = useState(false);
  const pending = useRef(null);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (pending.current) pending.current.cancel("Report search context changed");
      pending.current = null;
    };
  }, []);
  const cancel = () => {
    if (pending.current) pending.current.cancel("Report canceled");
    pending.current = null;
    setLoading(false);
    setOpen(false);
  };
  const submit = async event => {
    event.preventDefault();
    const reportRequest = request.trim();
    if (disabled || needsNewSearch || pending.current || !reportRequest || reportRequest.length > 2000) return;
    const cancellation = createAiSearchCancellation();
    pending.current = cancellation;
    const current = () => mounted.current && pending.current === cancellation;
    setLoading(true); setError(null); setResponse(null); setSubmitted(reportRequest);
    try {
      const result = await generateReport({ searchSessionId, reportRequest }, cancellation.token);
      if (!current()) return;
      if (!validResponse(result, searchSessionId)) throw new Error("Invalid report response");
      setResponse(result);
      setNeedsNewSearch(result.newSearchRequired === true || terminalStatuses.includes(result.status));
      if (!["SUCCEEDED", "PARTIAL", "DEGRADED", "NO_FINDINGS"].includes(result.status)) setError(statusMessage(result.status));
    } catch (failure) {
      if (!current()) return;
      const http = failure.response && failure.response.status;
      const body = failure.response && failure.response.data;
      // Error responses may retain safe partial documents, especially on HTTP 504.
      // Never display a response belonging to another session or arbitrary transport error text.
      const matchesSession = body && body.searchSessionId === searchSessionId;
      const status = matchesSession && reportStatuses.includes(body.status) ? body.status :
        ({ 400: "INVALID_REQUEST", 401: "SESSION_NOT_ACCESSIBLE", 403: "EMPTY_AUTHORIZED_SCOPE",
          404: "SESSION_NOT_ACCESSIBLE", 410: "SESSION_EXPIRED", 504: "TIMED_OUT", 503: "FAILED" }[http] || "FAILED");
      if (validResponse(body, searchSessionId)) setResponse(body);
      setNeedsNewSearch(terminalStatuses.includes(status) || (matchesSession && body.newSearchRequired === true));
      setError(statusMessage(status));
    } finally {
      if (current()) { pending.current = null; setLoading(false); }
    }
  };
  // openRequest: the Search screen's documents header opens the request form ("Generate report" lives there).
  useEffect(() => { if (openRequest) setOpen(true); }, [openRequest]);
  return <section className="chat-search-report" aria-label="Current search report" aria-busy={loading}>
    {!open ? (hideOpenButton ? null : <button type="button" className="chat-search-ai-action" disabled={disabled || needsNewSearch}
      onClick={() => setOpen(true)}>Generate report</button>) : <form onSubmit={submit} aria-label="Report request">
      <h4>Generate report from current search results</h4>
      <label htmlFor="searchReportRequest">Describe what you want analyzed</label>
      <textarea id="searchReportRequest" value={request} maxLength={2000} required disabled={loading || needsNewSearch}
        placeholder="Describe the information you want analyzed in these results."
        onChange={event => setRequest(event.target.value)} />
      <div className="report-actions">
        <button type="submit" className="chat-search-ai-action" disabled={disabled || loading || needsNewSearch || !request.trim() || request.trim().length > 2000}>
          {loading ? "Generating report..." : "Generate Report"}</button>
        <button type="button" className="chat-search-ai-action" onClick={cancel}>Cancel</button>
      </div>
    </form>}
    {loading && <p role="status">Generating a report from the current search results...</p>}
    {error && <p role="alert" className="report-status-warning">{error}</p>}
    {response && <ReportResult response={response} request={submitted} originalSearch={searchContext} errorShown={!!error} />}
  </section>;
}

// A new scope gets a new component instance; late replies cannot cross session boundaries.
export default function SearchSessionReport(props) {
  return props.searchSessionId ? <ReportPanel key={props.searchSessionId} {...props} /> : null;
}
